import type { AppBridge } from '@/modules/app-bridge/composables/AppBridge';
import type { UserStore } from '@/stores/userStore';
import type { RouteLocationNormalizedGeneric } from 'vue-router';

/**
 * Higher order function which returns a closure with access to certain nuxt helpers.
 *
 * Using hardcoded login URL instead of $i18n to avoid otherwise unnecessary plugin dependency,
 * this should be fine since these paths will always stay the same across all languages.
 *
 * @param appBridge AppBridge instance required to make login call.
 * @param userStore Store instance required to read and write user token.
 */
export const routeLoginInterceptor = (appBridge: AppBridge, userStore: UserStore) => {
  return async (to: RouteLocationNormalizedGeneric, from?: RouteLocationNormalizedGeneric) => {
    const $log = logger.createLogMethods('app-bridge:route-interceptor');

    $log.log(`Intercepting`, { to: to.path, from: from?.path });

    if (import.meta.server) {
      return true;
    }

    // NOTE: This condition is true during SSR, and for same page-navigation during CSR.
    // Otherwise, this interceptor only triggers for CSR navigations.
    if (to.path === from?.path) {
      return true;
    }

    const handleTokenResult = (token: any) => {
      if (token) {
        $log.log(`Received new token`, { token });
        userStore.setToken(token);

        // Redirects to given param
        if (userStore.token && to.query.redirectTo) {
          return to.query.redirectTo;
        }
      }

      // Aborts navigation during server-side page load, and redirects back to `from` during client navigation
      return false;
    };

    switch (to.path) {
      case '/login/':
        return handleTokenResult(await appBridge.actions.login());

      case '/register/':
        return handleTokenResult(await appBridge.actions.register());

      default:
    }
  };
};
